import { createFileRoute, Link } from '@tanstack/react-router';
import { usePlatformIdentity, withPlatformLinks } from '@/shared/platform/platformIdentity';
import { ActionIcon, Anchor, Group, ScrollArea, SimpleGrid, Text, TextInput, rem } from '@mantine/core';
import {
  IconArrowRight,
  IconBook,
  IconBrandGithub,
  IconChevronDown,
  IconExternalLink,
  IconHeartHandshake,
  IconHelp,
  IconHistory,
  IconLifebuoy,
  IconMail,
  IconSearch,
  IconShieldLock,
  IconTicket,
  IconX,
} from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { useMemo, useState, type CSSProperties } from 'react';
import Fuse from 'fuse.js';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { mw, MW_CARD_SHADOW } from '@/shared/mobile/tokens';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MobileHelp } from '@/features/content/mobile/MobileHelp';
import { ReportProblemModal } from '@/features/support/SegnalaProblemaModal';

export const Route = createFileRoute('/_app/help')({ component: HelpRoute });

/** Selettore: Aiuto mobile (MaeDesign) vs pagina desktop (hero + risorse + FAQ). */
function HelpRoute() {
  const isMobile = useIsMobileShell();
  return isMobile ? <MobileHelp /> : <HelpPage />;
}

/** Accento brand MaeDesign per le tile (icon-box tinto). */
export type Tone = 'bordeaux' | 'azzurro' | 'success' | 'warning' | 'ink';
export const TONES: Record<Tone, { bg: string; fg: string }> = {
  bordeaux: { bg: mw.brandTint, fg: mw.brand },
  azzurro: { bg: mw.accentTint, fg: mw.accentText },
  success: { bg: mw.successBg, fg: mw.success },
  warning: { bg: mw.warningBg, fg: mw.warning },
  ink: { bg: mw.surface2, fg: mw.ink500 },
};

export interface QuickLink {
  icon: typeof IconBook;
  label: string;
  description: string;
  tone: Tone;
  href?: string;
  to?: string;
  external?: boolean;
  /** Non naviga: apre una modale della pagina. `report` = «Segnala un problema». */
  action?: 'report';
}

export const QUICK_LINKS: QuickLink[] = [
  { icon: IconBook, label: 'Guida utente', description: 'Manuale operativo per gli utenti finali.', tone: 'azzurro', href: 'https://docs.maestrale.it/maefwk10', external: true },
  /* Dal 10-09-2026 le segnalazioni vanno alla scrivania dell'assistenza (argomento «Piattaforma»),
     non più al vecchio help desk: il tasto apre la stessa modale del menu. */
  { icon: IconLifebuoy, label: 'Segnala un problema', description: 'Un difetto o una proposta: arriva all’assistenza con pagina e browser già allegati.', tone: 'success', action: 'report' },
  { icon: IconMail, label: 'Contattaci', description: 'supporto@maestrale.it · risposta entro 24h lav.', tone: 'bordeaux', href: 'mailto:supporto@maestrale.it' },
  { icon: IconShieldLock, label: 'Privacy & GDPR', description: 'Informativa, consensi, esportazione dati.', tone: 'warning', to: '/legals' },
  { icon: IconBrandGithub, label: 'Note di rilascio', description: 'Le novità di ogni versione.', tone: 'ink', to: '/circulars/release-notes' },
  { icon: IconHistory, label: 'Audit personale', description: 'Cronologia dei tuoi accessi e azioni.', tone: 'azzurro', to: '/user-audit' },
];

export interface Faq { q: string; a: string; tags: string[] }

export const FAQS: Faq[] = [
  { q: 'Come compilo il timesheet della settimana?', a: 'Dal menu Timesheet, scegli la settimana, clicca sulla cella giorno/progetto e inserisci le ore. Le ore straordinarie vanno nel campo dedicato. Premi Salva o invio: la riga si chiude automaticamente.', tags: ['timesheet', 'ore', 'rendicontazione'] },
  { q: 'Come richiedo un giorno di smart working?', a: 'Vai su Smart working → Richiedi giornata. Seleziona la data e l\'eventuale fascia oraria, scegli l\'approvatore. La richiesta resta in coda finché il manager non la approva (riceverai notifica).', tags: ['smart working', 'sw', 'remote'] },
  { q: 'Come prenoto un\'auto aziendale?', a: 'Apri "Prenota mezzo" dall\'home oppure il menu Bookings → Strumenti calendar. Trascina sulla griglia per selezionare l\'intervallo, scegli il veicolo. Vedrai i conflitti in tempo reale.', tags: ['auto', 'veicolo', 'prenotazione', 'mezzo'] },
  { q: 'Come reset/cambio la password?', a: 'Dal login, clicca "Password dimenticata". Riceverai una mail con il link per impostarne una nuova. Se sei già loggato, vai su Impostazioni → Sicurezza.', tags: ['password', 'login', 'reset', 'accesso'] },
  { q: 'Cosa significa "stato fatturazione" sul timesheet?', a: 'Il badge indica se la voce è fatturabile, già fatturata, in revisione o non fatturabile. È determinato da regole sul progetto associato.', tags: ['fatturazione', 'billing', 'timesheet'] },
  { q: 'Le notifiche push non arrivano sul telefono.', a: 'Verifica i permessi notifiche dall\'OS, poi in Impostazioni → Notifiche assicurati che il canale sia attivo. Su Android alcuni produttori (Xiaomi, Huawei) richiedono autorizzazione battery exception.', tags: ['notifiche', 'push', 'mobile', 'android', 'ios'] },
  { q: 'Posso esportare i dati di un report?', a: 'Sì: nella pagina Reporting scegli il tipo di report, il periodo e usa i bottoni Excel/PDF/CSV. Anteprima fino a 100 righe; export completo.', tags: ['export', 'excel', 'pdf', 'csv', 'reporting'] },
  { q: 'Come funzionano i ruoli e i permessi?', a: 'Ogni utente è assegnato a uno o più ruoli, che combinano permessi su risorse e azioni. Solo gli amministratori dell’azienda possono modificare ruoli da Settings → Ruoli.', tags: ['ruoli', 'permessi', 'sicurezza', 'casl'] },
];

const CARD: CSSProperties = {
  border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW,
};

function HelpPage() {
  const [report, setReport] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query, 200);
  const [tag, setTag] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const fuse = useMemo(() => new Fuse(FAQS, { keys: ['q', 'a', 'tags'], threshold: 0.35 }), []);

  const topTags = useMemo(() => {
    const freq = new Map<string, number>();
    for (const f of FAQS) for (const t of f.tags) freq.set(t, (freq.get(t) ?? 0) + 1);
    return [...freq.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8).map(([t]) => t);
  }, []);

  const filteredFaqs = useMemo(() => {
    let base = debounced.trim() ? fuse.search(debounced).map((r) => r.item) : FAQS;
    if (tag) base = base.filter((f) => f.tags.includes(tag));
    return base;
  }, [debounced, fuse, tag]);

  const platform = usePlatformIdentity();
  const quickLinks = useMemo(() => withPlatformLinks(QUICK_LINKS, platform), [platform]);
  const filteredLinks = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    if (!q) return quickLinks;
    return quickLinks.filter((l) => l.label.toLowerCase().includes(q) || l.description.toLowerCase().includes(q));
  }, [debounced, quickLinks]);

  /* La modale legge da sé la pagina corrente: non serve passargliela. */
  const openTicketForCurrentPage = () => setReport(true);

  const chip = (label: string, active: boolean, onClick: () => void) => (
    <button key={label} type="button" className="mw-pressable" onClick={onClick}
      style={{ height: rem(30), padding: '0 12px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-compact)', fontWeight: 600, whiteSpace: 'nowrap', border: active ? `1px solid ${mw.brand}` : `1px solid ${mw.line}`, background: active ? mw.brandFill : mw.surface, color: active ? 'var(--on-brand)' : mw.ink500 }}>
      {label}
    </button>
  );

  return (
    <MaeShell>
      <MaePageHead
        title="Aiuto"
        sub="Risorse di supporto, FAQ e canali di contatto."
        icon={<IconHelp size={23} />}
        tone="azzurro"
        actions={
          <Anchor href={platform.statusUrl ?? undefined} target="_blank" rel="noopener noreferrer" underline="never">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: rem(36), padding: '0 14px', borderRadius: 999, border: `1px solid ${mw.line}`, background: mw.surface, fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink500 }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: mw.success, boxShadow: `0 0 0 3px ${mw.successBg}` }} />
              Stato servizio
            </span>
          </Anchor>
        }
      />

      {/* Hero ricerca */}
      <div style={{ borderRadius: 18, padding: '22px 22px', marginBottom: 18, background: 'linear-gradient(135deg, var(--brand-fill) 0%, var(--brand-grad-2) 100%)', boxShadow: '0 10px 26px rgba(84,4,9,0.22)' }}>
        <Text style={{ fontSize: 'var(--fs-h4)', fontWeight: 700, color: 'var(--on-brand)', letterSpacing: '-0.01em' }}>Come possiamo aiutarti?</Text>
        <Text style={{ fontSize: 'var(--fs-compact)', color: 'rgba(255,255,255,0.82)', marginTop: 2, marginBottom: 14 }}>Cerca tra FAQ e risorse, o apri un ticket di supporto.</Text>
        <TextInput
          placeholder="Cerca (es. timesheet, ferie, password)…"
          leftSection={<IconSearch size={17} />}
          rightSection={query ? (
            <ActionIcon variant="subtle" color="gray" onClick={() => setQuery('')} aria-label="Cancella"><IconX size={14} /></ActionIcon>
          ) : null}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          size="md"
          styles={{ input: { border: 0, boxShadow: MW_CARD_SHADOW, fontSize: 'var(--fs-body-lg)' } }}
        />
      </div>

      {/* Risorse rapide */}
      {filteredLinks.length > 0 && (
        <>
          <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: mw.ink300, marginBottom: 10 }}>Risorse rapide</Text>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing={14} mb={22}>
            {filteredLinks.map((l) => {
              const t = TONES[l.tone];
              const inner = (
                <div className="mw-pressable" style={{ ...CARD, padding: 18, height: '100%', cursor: 'pointer', display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                  <span style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: t.bg, color: t.fg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><l.icon size={22} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={5} wrap="nowrap">
                      <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 700, color: mw.ink900 }}>{l.label}</Text>
                      {l.external && <IconExternalLink size={12} style={{ color: mw.ink300 }} />}
                    </Group>
                    <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 3, lineHeight: 1.45 }} lineClamp={2}>{l.description}</Text>
                  </div>
                  <IconArrowRight size={16} style={{ color: mw.ink300, flexShrink: 0, marginTop: 4 }} />
                </div>
              );
              if (l.action === 'report') {
                return (
                  <button key={l.label} type="button" onClick={() => setReport(true)}
                    style={{ all: 'unset', display: 'block', cursor: 'pointer' }}>
                    {inner}
                  </button>
                );
              }
              return l.to ? (
                <Link key={l.label} to={l.to} style={{ textDecoration: 'none' }}>{inner}</Link>
              ) : (
                <Anchor key={l.label} href={l.href} target={l.external ? '_blank' : undefined} rel={l.external ? 'noopener noreferrer' : undefined} underline="never">{inner}</Anchor>
              );
            })}
          </SimpleGrid>
        </>
      )}

      {/* FAQ */}
      <div style={{ ...CARD, overflow: 'hidden', marginBottom: 18 }}>
        <Group justify="space-between" p={18} pb={12}>
          <Group gap={10}>
            <span style={{ width: 34, height: 34, borderRadius: 10, background: mw.accentTint, color: mw.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconHeartHandshake size={18} /></span>
            <div>
              <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>Domande frequenti</Text>
              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>{filteredFaqs.length} risultati</Text>
            </div>
          </Group>
        </Group>

        {/* Filtro per argomento */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 18px 14px' }}>
          {chip('Tutte', tag === null, () => setTag(null))}
          {topTags.map((t) => chip(t, tag === t, () => setTag(tag === t ? null : t)))}
        </div>

        {filteredFaqs.length === 0 ? (
          <div style={{ padding: '28px 18px', textAlign: 'center', borderTop: `1px solid ${mw.line}` }}>
            <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 700, color: mw.ink900 }}>Nessuna FAQ corrisponde</Text>
            <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 4 }}>Non abbiamo risposte per «{debounced || tag}». Segnala il problema all’assistenza.</Text>
            <button type="button" className="mw-pressable" onClick={openTicketForCurrentPage}
              style={{ marginTop: 14, height: rem(38), padding: '0 18px', borderRadius: 10, border: 0, cursor: 'pointer', background: mw.brandFill, color: 'var(--on-brand)', fontSize: 'var(--fs-body)', fontWeight: 700, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <IconTicket size={16} /> Segnala un problema
            </button>
          </div>
        ) : (
          <ScrollArea.Autosize mah={560}>
            <div style={{ borderTop: `1px solid ${mw.line}` }}>
              {filteredFaqs.map((f) => {
                const isOpen = open === f.q;
                return (
                  <div key={f.q} style={{ borderBottom: `1px solid ${mw.line}` }}>
                    <button type="button" onClick={() => setOpen(isOpen ? null : f.q)}
                      style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px', border: 0, background: isOpen ? mw.surface2 : 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 600, color: mw.ink900 }}>{f.q}</Text>
                      <IconChevronDown size={16} style={{ color: mw.ink300, flexShrink: 0, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                    </button>
                    {isOpen && (
                      <div style={{ padding: '0 18px 16px' }}>
                        <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, lineHeight: 1.6 }}>{f.a}</Text>
                        <Group gap={6} mt={10}>
                          {f.tags.map((tg) => (
                            <span key={tg} onClick={() => setTag(tg)} style={{ cursor: 'pointer', fontSize: 'var(--fs-compact)', fontWeight: 600, padding: '2px 9px', borderRadius: 999, background: mw.accentTint, color: mw.accentText }}>{tg}</span>
                          ))}
                        </Group>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea.Autosize>
        )}
      </div>

      {/* Banda contatti / supporto */}
      <div style={{ ...CARD, padding: 20, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between', background: mw.surface2 }}>
        <Group gap={13} wrap="nowrap">
          <span style={{ width: 46, height: 46, borderRadius: 13, background: mw.brandTint, color: mw.brand, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IconLifebuoy size={24} /></span>
          <div>
            <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>Non hai trovato risposta?</Text>
            <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>Il team di supporto risponde entro 24h lavorative.</Text>
          </div>
        </Group>
        <Group gap={10}>
          <Anchor href={`mailto:${platform.supportEmail ?? ''}`} underline="never">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: rem(40), padding: '0 16px', borderRadius: 10, border: `1px solid ${mw.line}`, background: mw.surface, color: mw.ink900, fontSize: 'var(--fs-body)', fontWeight: 600 }}><IconMail size={16} /> Email</span>
          </Anchor>
          <button type="button" className="mw-pressable" onClick={openTicketForCurrentPage}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: rem(40), padding: '0 18px', borderRadius: 10, border: 0, cursor: 'pointer', background: mw.brandFill, color: 'var(--on-brand)', fontSize: 'var(--fs-body)', fontWeight: 700, fontFamily: 'inherit' }}>
            <IconTicket size={16} /> Segnala un problema
          </button>
        </Group>
      </div>

      <ReportProblemModal opened={report} onClose={() => setReport(false)} />
    </MaeShell>
  );
}
